import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Typography, TextField, Button, Paper, IconButton, Grid, Chip, Card,
  CardContent, InputAdornment, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Alert, Fade, Tooltip, CircularProgress, Menu, MenuItem, 
  ListItemIcon, Dialog, DialogTitle, DialogContent, DialogActions, Zoom
} from "@mui/material";

// Icons
import {
  DeleteOutline as DeleteIcon,
  AddModerator as AddIcon,
  Search as SearchIcon,
  DomainVerification as DomainIcon,
  HistoryEdu as ReasonIcon,
  Refresh as RefreshIcon,
  Sort as SortIcon,
  CalendarToday as DateIcon,
  Abc as NameIcon,
  EditOutlined as EditIcon,
  AdminPanelSettings as AdminIcon,
  Close as CloseIcon
} from "@mui/icons-material";

import AdminDashboardLayout from "../layout/AdminDashboardLayout";
import {
  getBlockedDomains,
  addBlockedDomain,
  updateBlockedDomain,
  deleteBlockedDomain,
} from "../../../services/adminBlockedDomainService";

import "./BlockedDomains.css";

const SUGGESTED_REASONS = [
  "Disposable / Temporary Email",
  "Spam / Fraud",
  "Internal Company Restriction",
  "Competitor Domain"
];

export default function BlockedDomains() {
  const [domains, setDomains] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({ domain: "", reason: "" });
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingDomain, setEditingDomain] = useState(null);
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });

  const fetchDomains = async () => {
    try {
      setFetchLoading(true);
      const { data } = await getBlockedDomains();
      setDomains(data || []);
    } catch (err) {
      setError("System was unable to synchronize restricted data.");
    } finally {
      setFetchLoading(false);
    }
  };

  useEffect(() => { fetchDomains(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!formData.domain.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await addBlockedDomain(formData);
      setFormData({ domain: "", reason: "" });
      fetchDomains();
    } catch (err) {
      setError("Protocol Error: Domain already exists in database.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("CONFIRMATION: Revoke restriction for this domain?")) {
      try {
        await deleteBlockedDomain(id);
        fetchDomains();
      } catch (err) { setError("Operation failed."); }
    }
  };

  const handleEditOpen = (domain) => {
    setEditingDomain({ ...domain });
    setEditModalOpen(true);
  };

  const handleUpdate = async () => {
    try {
      await updateBlockedDomain(editingDomain._id, {
        domain: editingDomain.domain,
        reason: editingDomain.reason,
      });
      setEditModalOpen(false);
      fetchDomains();
    } catch (err) {
      setError("Failed to update restriction.");
    }
  };

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'desc' ? 'asc' : 'desc';
    setSortConfig({ key, direction });
    setAnchorEl(null);
  };

  const processedDomains = useMemo(() => {
    let filtered = domains.filter(d => 
      d.domain.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.reason?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return [...filtered].sort((a, b) => {
      let valA = a[sortConfig.key] || "";
      let valB = b[sortConfig.key] || "";
      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [domains, searchTerm, sortConfig]);

  return (
    <AdminDashboardLayout>
      <Box className="blocked-page-container">
        <Box
          sx={{
            display: "flex",
            height: "calc(100vh - 72px)",
            maxWidth: "100vw",
            overflowX: "hidden"
          }}
        >
          {/* LEFT SECTION */}
          <Box
            sx={{
              width: 520,
              flexShrink: 0,
              borderRight: "1px solid #e2e8f0",
              bgcolor: "#fff",
              p: 4,
              overflowY: "auto"
            }}
          >
            <Box className="sticky-action-panel">
              <Box mb={4}>
                <Typography variant="h5" fontWeight={900} sx={{ color: '#0f172a', letterSpacing: "-1px" }}>
                  DOMAIN BLACKLIST DASHBOARD
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
                  Admin Security Console 
                </Typography>
              </Box>

              <Card elevation={0} sx={{ bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <form onSubmit={handleAdd}>
                    <Box display="flex" alignItems="center" gap={1} mb={3}>
                      <AddIcon sx={{ color: '#2563eb', fontSize: 20 }} />
                      <Typography variant="subtitle2" fontWeight={800}>Create Restriction</Typography>
                    </Box>

                    {error && <Alert severity="error" sx={{ mb: 2, py: 0 }}>{error}</Alert>}

                    <TextField
                      fullWidth label="Domain Name"
                      placeholder="spamsite.com"
                      value={formData.domain}
                      onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                      required size="small" sx={{ mb: 3, bgcolor: '#fff' }}
                      InputProps={{ startAdornment: <InputAdornment position="start"><DomainIcon fontSize="small" /></InputAdornment> }}
                    />

                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, mb: 1, display: 'block' }}>
                      SUGGESTED REASONS:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      {SUGGESTED_REASONS.map(reason => (
                        <Chip 
                          key={reason} 
                          label={reason} 
                          size="small" 
                          onClick={() => setFormData({...formData, reason})}
                          sx={{ cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600 }}
                        />
                      ))}
                    </Box>

                    <TextField
                      fullWidth label="Restriction Reason"
                      placeholder="Note down why this domain is blocked..."
                      multiline rows={5}
                      value={formData.reason}
                      onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                      size="small" sx={{ mb: 4, bgcolor: '#fff' }}
                    />

                    <Button
                      fullWidth type="submit" variant="contained" 
                      className="btn-execute" disabled={loading}
                    >
                      {loading ? <CircularProgress size={22} color="inherit" /> : "Restrict Domain"}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              <Box mt={5} className="stat-card">
                <Typography variant="overline" color="text.secondary" fontWeight={800}>Current Enforcement</Typography>
                <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                  <Typography variant="h4" fontWeight={900}>{domains.length}</Typography>
                  <Chip label="Live Restrictions" size="small" className="status-chip" />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* RIGHT SECTION */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              bgcolor: "#f8fafc",
              p: 3,
              display: "flex"
            }}
          >
            <Paper className="main-data-paper" elevation={0} sx={{ width: '100%' }}>
              <Box className="grid-header-actions">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                  <TextField
                    placeholder="Search logs..."
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ width: '100%', maxWidth: 450, bgcolor: '#fff' }}
                    InputProps={{ 
                      startAdornment: <SearchIcon sx={{ color: '#94a3b8', mr: 1 }} fontSize="small" />,
                    }}
                  />
                  <IconButton onClick={fetchDomains} className="refresh-icon-btn">
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Box>
                
                <Button 
                  startIcon={<SortIcon />} 
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                  variant="outlined" 
                  className="sort-menu-btn"
                >
                  Sort
                </Button>

                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                  <MenuItem onClick={() => handleSort('domain')}><ListItemIcon><NameIcon fontSize="small"/></ListItemIcon>Domain Name</MenuItem>
                  <MenuItem onClick={() => handleSort('createdAt')}><ListItemIcon><DateIcon fontSize="small"/></ListItemIcon>Date Added</MenuItem>
                </Menu>
              </Box>

              <TableContainer className="fixed-table-scroll">
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>DOMAIN</TableCell>
                      <TableCell>ADDED BY</TableCell>
                      <TableCell>REASON</TableCell>
                      <TableCell align="right">ACTIONS</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fetchLoading ? (
                      <TableRow><TableCell colSpan={4} align="center" sx={{ py: 15 }}><CircularProgress size={30} /></TableCell></TableRow>
                    ) : processedDomains.map((item) => (
                      <TableRow key={item._id} hover className="table-row-ui">
                        <TableCell>
                          <Typography className="mono-domain-text">@{item.domain}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <AdminIcon sx={{ fontSize: 16, color: '#64748b' }} />
                            <Typography variant="body2" fontWeight={600} color="#475569">
                             {item.addedBy?.username || "Admin"}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography className="reason-truncate">
                            {item.reason || "Policy enforcement"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Box display="flex" justifyContent="flex-end" gap={0.5}>
                            <Tooltip title="View/Edit Details">
                              <IconButton onClick={() => handleEditOpen(item)} sx={{ color: '#6366f1' }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton onClick={() => handleDelete(item._id)} sx={{ color: '#f87171' }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        </Box>

        {/* EDIT MODAL */}
        <Dialog 
          open={editModalOpen} 
          onClose={() => setEditModalOpen(false)}
          TransitionComponent={Zoom}
          fullWidth
          maxWidth="sm"
          PaperProps={{ sx: { borderRadius: 4, p: 1 } }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
            <Typography variant="h6" fontWeight={900}>Restriction Details</Typography>
            <IconButton onClick={() => setEditModalOpen(false)} size="small"><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField 
                fullWidth label="Domain Name" 
                value={editingDomain?.domain || ""} 
                onChange={(e) => setEditingDomain({...editingDomain, domain: e.target.value})}
              />
              <TextField 
                fullWidth label="Incident Reason" 
                multiline rows={4} 
                value={editingDomain?.reason || ""} 
                onChange={(e) => setEditingDomain({...editingDomain, reason: e.target.value})}
              />
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8fafc', borderStyle: 'dashed' }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>ADDED BY</Typography>
                    <Typography variant="body2" fontWeight={600}> {editingDomain?.addedBy?.username || "Admin"}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>ENFORCEMENT DATE</Typography>
                    <Typography variant="body2" fontWeight={600}>{new Date(editingDomain?.createdAt).toLocaleDateString()}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setEditModalOpen(false)} sx={{ color: '#64748b', fontWeight: 700 }}>Cancel</Button>
            <Button 
              onClick={handleUpdate} 
              variant="contained" 
              sx={{ bgcolor: '#000', borderRadius: 2, px: 4, fontWeight: 800, "&:hover": { bgcolor: '#222' } }}
            >
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminDashboardLayout>
  );
}